import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Scale, CheckCircle2, ShieldCheck, Sparkles, Loader2, ArrowRight } from "lucide-react";
import { CityPicker } from "@/components/CityPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { SpecializationPicker } from "@/components/SpecializationPicker";


const LANGUAGES = ["English", "Hindi", "Marathi", "Tamil", "Telugu", "Bengali", "Gujarati", "Punjabi", "Kannada", "Malayalam", "Urdu"];
const CITY_UNAVAILABLE_MESSAGE = "We’re not currently available in this location. We’re expanding soon.";

const PERKS = [
  { icon: ShieldCheck, title: "Trusted clients", body: "Every consultation comes from a verified Advoset user — pre-screened, paid, ready to talk." },
  { icon: Sparkles, title: "Zero setup fees", body: "We earn only when you do. Transparent commission, no hidden charges." },
  { icon: CheckCircle2, title: "Tools that save you time", body: "AI memo, live chat, audio/video — all built-in. No third-party tools." },
];

export default function BecomeALawyer() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", phone: "+91", city: "", city_id: "", city_state: "", whatsapp_opt_in: true,
    city_is_serviceable: null,
    specializations: ["Family Law"], experience: 1,
    bci_enrolment_number: "", languages: ["English", "Hindi"],
    bio: "", referral_source: "",
    requested_pricing_level: "", pricing_custom_fee_rupees: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});
  const [levels, setLevels] = useState([]);

  useEffect(() => {
    api.get("/pricing/levels").then((r) => setLevels(r.data.levels || [])).catch(() => setLevels([]));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleLang = (l) => set("languages", form.languages.includes(l)
    ? form.languages.filter((x) => x !== l)
    : [...form.languages, l]);

  const validate = () => {
    const e = {};
    if (!form.name.trim() || form.name.trim().length < 2) e.name = "Required (min 2 chars)";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) e.email = "A valid email is required";
    const cleaned = form.phone.replace(/\s/g, "");
    if (!/^\+?[1-9]\d{7,14}$/.test(cleaned)) e.phone = "E.164 phone e.g. +919876543210";
    if (!form.city_id) e.city = "Please search and select your city";
    else if (!form.city_is_serviceable) e.city = CITY_UNAVAILABLE_MESSAGE;
    if (!form.specializations.length) e.specializations = "Select at least one specialization";
    if (!form.bci_enrolment_number.trim() || form.bci_enrolment_number.trim().length < 3) e.bci_enrolment_number = "Required (e.g. MAH/12345/2020)";
    if (form.experience < 0 || form.experience > 70) e.experience = "0–70";
    if (Number(form.requested_pricing_level) === 11 && !Number(form.pricing_custom_fee_rupees)) {
      e.pricing = "A proposed custom fee is required for Level 11";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post("/lawyer/apply", {
        ...form,
        specialization: form.specializations[0] || "",
        phone: form.phone.replace(/\s/g, ""),
        experience: Number(form.experience),
        requested_pricing_level: form.requested_pricing_level ? Number(form.requested_pricing_level) : null,
        pricing_custom_fee_rupees: form.pricing_custom_fee_rupees ? Number(form.pricing_custom_fee_rupees) : null,
      });
      setDone(true);
      toast.success("Application submitted — we'll reach out within 2 business days");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not submit. Please try again.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#0A2540] text-white grid place-items-center p-6" data-testid="apply-success">
        <div className="max-w-lg w-full bg-white text-[#0A2540] rounded-2xl p-8 shadow-xl">
          <CheckCircle2 size={48} className="text-emerald-600 mx-auto" />
          <h1 className="mt-4 text-2xl font-extrabold text-center" style={{ fontFamily: "Poppins" }}>
            Application received
          </h1>
          <p className="mt-2 text-center text-slate-600">
            Thank you for applying to Advoset. Our onboarding team will review your application within
            <strong> 2 business days</strong> and email you next steps.
          </p>
          <p className="mt-4 text-xs text-center text-slate-400">
            When approved, we'll email <strong>{form.email}</strong> — then sign in from{" "}
            <a href="/lawyer-portal/login" className="font-bold text-[#0A2540] underline">
              advoset.in/lawyer-portal
            </a>{" "}
            using your Google account with the same email.
          </p>
          <Button onClick={() => nav("/")} data-testid="apply-go-home" className="mt-6 w-full rounded-xl adv-btn-primary">
            Back to home <ArrowRight size={14} className="ml-1" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A2540] text-white" data-testid="become-lawyer-page">
      {/* Header */}
      <header className="max-w-6xl mx-auto flex items-center justify-between px-6 py-5">
        <Link to="/" className="flex items-center gap-2 group" data-testid="apply-back-home">
          <Scale size={22} className="text-[#D4AF37]" />
          <span className="font-extrabold tracking-tight" style={{ fontFamily: "Poppins" }}>Advoset</span>
        </Link>
        <Link to="/lawyer-portal/login" className="text-xs text-slate-300 hover:text-[#D4AF37]" data-testid="apply-already-onboarded">
          Already onboarded? Lawyer login →
        </Link>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-6 pb-10 grid lg:grid-cols-2 gap-10 items-start">
        <div>
          <p className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-3 py-1">
            <Sparkles size={12} /> India-first AI + verified lawyers
          </p>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05]" style={{ fontFamily: "Poppins" }}>
            Practice on <span className="text-[#D4AF37]">Advoset</span>.<br />
            Built for advocates of India.
          </h1>
          <p className="mt-5 text-slate-300 max-w-md">
            Join 150+ verified advocates earning from chat, audio &amp; video consultations.
            We handle bookings, payments, KYC &amp; client trust — you focus on practice.
          </p>

          <div className="mt-8 space-y-4 max-w-md">
            {PERKS.map(({ icon: Icon, title, body }) => (
              <div key={title} className="flex gap-3" data-testid={`perk-${title.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="shrink-0 h-9 w-9 rounded-xl bg-[#D4AF37]/15 border border-[#D4AF37]/30 grid place-items-center">
                  <Icon size={16} className="text-[#D4AF37]" />
                </div>
                <div>
                  <p className="font-bold">{title}</p>
                  <p className="text-xs text-slate-300">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={onSubmit}
          className="bg-white text-[#0A2540] rounded-2xl p-6 sm:p-8 shadow-2xl"
          data-testid="apply-form"
        >
          <h2 className="text-lg font-extrabold" style={{ fontFamily: "Poppins" }}>Apply to join</h2>
          <p className="text-xs text-slate-500 mt-0.5">Takes ~3 minutes · we'll reply within 2 business days</p>

          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            <Field label="Full name (as on BCI)" error={errors.name}>
              <Input data-testid="apply-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Adv. Priya Sharma" />
            </Field>
            <Field label="Email" error={errors.email}>
              <Input data-testid="apply-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="priya@example.com" />
            </Field>
            <Field label="Phone (E.164)" error={errors.phone}>
              <Input data-testid="apply-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+919876543210" />
            </Field>
            <Field label="City / Practice location *" error={errors.city}>
              <CityPicker
                testid="apply-city"
                showServiceability
                value={form.city_id ? { city_id: form.city_id, city_name: form.city, state_name: form.city_state } : null}
                onChange={(c) => {
                  setForm((f) => ({
                    ...f,
                    city_id: c ? c.id : "",
                    city: c ? c.name : "",
                    city_state: c ? c.state_name : "",
                    city_is_serviceable: c ? c.is_serviceable : null,
                  }));
                  setErrors((previous) => ({
                    ...previous,
                    city: c && !c.is_serviceable ? CITY_UNAVAILABLE_MESSAGE : "",
                  }));
                }}
              />
            </Field>
            <Field label="Specializations" error={errors.specializations}>
              <SpecializationPicker value={form.specializations} onChange={(v) => set("specializations", v)} testid="apply-specialization" />
            </Field>
            <Field label="Years of experience" error={errors.experience}>
              <Input data-testid="apply-experience" type="number" min={0} max={70}
                value={form.experience} onChange={(e) => set("experience", e.target.value)} />
            </Field>
            <Field label="BCI enrolment number" error={errors.bci_enrolment_number} colSpan>
              <Input data-testid="apply-bci"
                value={form.bci_enrolment_number}
                onChange={(e) => set("bci_enrolment_number", e.target.value)}
                placeholder="MAH/12345/2020" />
            </Field>
            <Field label="Preferred pricing level (optional)" error={errors.pricing}>
              <select
                data-testid="apply-pricing-level"
                value={form.requested_pricing_level}
                onChange={(e) => set("requested_pricing_level", e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
              >
                <option value="">Decide later (from your portal)</option>
                {levels.map((l) => (
                  <option key={l.level} value={l.level}>
                    Level {l.level} — ₹{l.base_fee_rupees.toLocaleString("en-IN")} (video · 15 min)
                  </option>
                ))}
                <option value="11">Level 11 — Custom fee (admin review)</option>
              </select>
              <p className="mt-1 text-[11px] text-slate-400">Your level is activated only after admin approval.</p>
            </Field>
            {Number(form.requested_pricing_level) === 11 && (
              <Field label="Proposed custom fee (₹, video · 15 min)" error={errors.pricing}>
                <Input data-testid="apply-pricing-custom-fee" type="number" min={1}
                  value={form.pricing_custom_fee_rupees}
                  onChange={(e) => set("pricing_custom_fee_rupees", e.target.value)}
                  placeholder="2500" />
              </Field>
            )}
          </div>

          <div className="mt-4">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Languages you practice in</p>
            <div className="flex flex-wrap gap-1.5" data-testid="apply-languages">
              {LANGUAGES.map((l) => {
                const on = form.languages.includes(l);
                return (
                  <button type="button" key={l} onClick={() => toggleLang(l)}
                    data-testid={`apply-lang-${l.toLowerCase()}`}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition ${
                      on ? "bg-[#0A2540] text-[#D4AF37] border-[#0A2540]" : "bg-white text-slate-600 border-slate-200 hover:border-[#0A2540]"
                    }`}>
                    {l}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-4">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-1">Short bio (optional)</p>
            <Textarea data-testid="apply-bio" rows={3} maxLength={2000}
              value={form.bio} onChange={(e) => set("bio", e.target.value)}
              placeholder="Tell clients what you specialise in, your highlights, anything noteworthy." />
          </div>

          <div className="mt-4">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-1">How did you hear about us? (optional)</p>
            <Input data-testid="apply-referral" value={form.referral_source}
              onChange={(e) => set("referral_source", e.target.value)} placeholder="LinkedIn / Friend / Search" />
          </div>

          <label className="mt-4 flex items-start gap-2 cursor-pointer select-none" data-testid="apply-whatsapp-optin-label">
            <input type="checkbox" checked={form.whatsapp_opt_in}
              onChange={(e) => set("whatsapp_opt_in", e.target.checked)}
              data-testid="apply-whatsapp-optin" className="mt-0.5 h-4 w-4 accent-[#0A2540]" />
            <span className="text-xs text-slate-600">
              Send me updates on WhatsApp about my application, verification and onboarding.
            </span>
          </label>

          <Button type="submit" disabled={submitting} data-testid="apply-submit-btn"
            className="mt-6 w-full h-11 rounded-xl adv-btn-gold font-extrabold">
            {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
            Submit application
          </Button>
          <p className="mt-3 text-[11px] text-slate-400 text-center">
            By submitting you agree to a manual review of your BCI enrolment and KYC documents.
          </p>
        </form>
      </section>

      <footer className="border-t border-white/5 mt-10 py-6 text-center text-[11px] text-slate-500">
        Advoset Legal Help, Simplified · Verified Bar Council enrolment required
      </footer>
    </div>
  );
}

function Field({ label, error, children, colSpan }) {
  return (
    <div className={colSpan ? "sm:col-span-2" : ""}>
      <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-1">{label}</p>
      {children}
      {error && <p className="mt-1 text-[11px] text-rose-600">{error}</p>}
    </div>
  );
}
